// Config and hooks for the `math` sanitize preset in `@github-ui/sanitize`.
// Selected via `sanitize(html, {preset: 'math'})`; use it to sanitize the MathML
// that MathJax renders from `@github-ui/math-renderer-element`.
//
// Unlike the other presets, `math` DELIBERATELY does not extend `SHARED_CONFIG`:
// the shared base forbids inline `style`, but MathJax's rendered MathML relies on
// inline `style` to position glyphs, so this config keeps it. The config is
// therefore standalone — the MathML tag profile plus the `mjx-*` / `TEX-` custom
// elements MathJax emits, hardened by `addMathSanitizeHooks` (a MathJax-only class
// filter and a `columnalign`-on-`<mtable>` force-keep). Output is minted through
// the dedicated `github-sanitize-math` Trusted Types policy (see `trusted-types.ts`);
// a strict preset never reuses the lax `github-sanitize-lax` policy.
//
// Lifted verbatim from `@github-ui/math-renderer-element`'s bespoke DOMPurify config
// (spike github/react-platform#26208, decision D6). The math-renderer-element
// adoption (github/react-platform#2030) routes through this preset, re-using
// `MATH_SANITIZE_CONFIG` + `addMathSanitizeHooks` for the one intermediate
// `RETURN_DOM: true` pass that cannot go through the closed `{preset}` path.

import type {Config, Purifier, UponSanitizeAttributeHookEvent} from '../types'

// Matches the custom element tag/attribute prefixes MathJax emits (`TEX-`, `mjx-`,
// `MJX-`). Reused for both DOMPurify's custom-element allowlist and the class filter.
// Note: DOMPurify lowercases tag/attribute names before applying `tagNameCheck` /
// `attributeNameCheck`, so on the DOM path only the lowercase `mjx-` family is
// effectively matched there. The uppercase `TEX`/`MJX` alternatives exist for the
// class filter (`sanitizeClass`), which sees raw, case-preserved class tokens.
const MATHJAX_PREFIX_REGEX = /^(TEX|mjx|MJX)-/

// Recursively freeze a config object so the publicly re-exported `MATH_SANITIZE_CONFIG`
// cannot be mutated by a consumer (e.g. `MATH_SANITIZE_CONFIG.ADD_ATTR?.push(...)`),
// which would silently weaken every later `{preset: 'math'}` call that reads the same
// object. RegExp values are skipped: DOMPurify resets `lastIndex` on the custom-element
// check regexes before each `.test()`, which throws on a frozen RegExp. Freezing the
// containing objects still blocks reassignment, and DOMPurify only reads the config.
function deepFreeze<T>(value: T): T {
  if (value && typeof value === 'object' && !(value instanceof RegExp)) {
    for (const key of Object.keys(value)) {
      deepFreeze((value as Record<string, unknown>)[key])
    }
    Object.freeze(value)
  }
  return value
}

// Math config — the MathML tag profile plus the MathJax custom elements and the
// handful of presentation attributes its rendered output needs. Standalone by
// design (see file header): it does NOT layer on `SHARED_CONFIG`, so inline `style`
// survives. Deep-frozen because it is both re-exported publicly (for the #2030
// intermediate `RETURN_DOM` pass) and shared by the closed preset path.
export const MATH_SANITIZE_CONFIG: Readonly<Config> = deepFreeze({
  USE_PROFILES: {mathMl: true},
  ADD_ATTR: ['jax', 'unselectable', 'class', 'style', 'size', 'justify', 'space'],
  KEEP_CONTENT: true,
  CUSTOM_ELEMENT_HANDLING: {
    tagNameCheck: MATHJAX_PREFIX_REGEX,
    attributeNameCheck: MATHJAX_PREFIX_REGEX,
    allowCustomizedBuiltInElements: true,
  },
})

// Force DOMPurify to keep `columnalign` on `<mtable>` — the MathML profile drops it
// otherwise, and MathJax relies on it for column alignment. Compares a lowercased
// tag name so the force-keep fires identically on the browser (`mtable`) and the
// server, where linkedom reports an uppercased `tagName` (`MTABLE`).
function allowMathMlAttributes(el: Element, data: UponSanitizeAttributeHookEvent): void {
  const allowedTags = ['mtable']
  const allowedAttributes = ['columnalign']

  if (allowedTags.includes(el.tagName.toLowerCase()) && allowedAttributes.includes(data.attrName)) {
    data.keepAttr = true
    data.forceKeepAttr = true
  }
}

// Restrict `class` tokens to the MathJax-prefixed names (plus the `MathJax` and
// `js-inline-math` markers), dropping any other class an attacker might inject.
function sanitizeClass(_el: Element, data: UponSanitizeAttributeHookEvent): void {
  if (data.attrName !== 'class') {
    return
  }

  // Split on ALL whitespace, not just literal spaces: the browser tokenizes class
  // attributes on any whitespace (newline/tab included), so `mjx-a\njs-evil` exposes
  // two classes. Splitting on ` ` alone would treat that as one token starting with
  // `mjx-`, keeping the injected `js-evil`. Filter empties from leading/trailing
  // whitespace before rejoining.
  data.attrValue = data.attrValue
    .split(/\s+/)
    .filter(c => c !== '' && (MATHJAX_PREFIX_REGEX.test(c) || c === 'MathJax' || c === 'js-inline-math'))
    .join(' ')

  // Drop the attribute entirely when every token was filtered out.
  if (data.attrValue === '') {
    data.keepAttr = false
  }
}

/**
 * Attach the math preset's security hooks to a purifier instance: the MathJax-only
 * class filter and the `columnalign`-on-`<mtable>` force-keep. Isomorphic — the
 * hooks only inspect the node/attribute DOMPurify hands them, so the server
 * (linkedom) path is a safe no-op when nothing matches. Called from each entry's
 * local `createPurifier`, and re-exported so the math-renderer-element adoption
 * (#2030) can build the same purifier for its intermediate `RETURN_DOM` pass.
 */
export function addMathSanitizeHooks(instance: Purifier): void {
  instance.addHook('uponSanitizeAttribute', sanitizeClass)
  instance.addHook('uponSanitizeAttribute', allowMathMlAttributes)
}
